
/**
 * Utility functions for managing prediction objects and data structures
 */

/**
 * Creates a prediction object with the specified properties
 * @param {string} text - The prediction content
 * @param {string} type - The response type (optimistic/pessimistic/etc)
 * @param {number} level - The nesting depth (1, 2, or 3)
 * @param {string|null} parentId - Optional parent prediction ID
 * @returns {Object} Prediction object
 */
export const createPredictionObject = (text, type, level, parentId = null) => {
  // Generate unique ID using timestamp + random number
  const id = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Validate level is within expected range
  if (level < 1 || level > 3) {
    throw new Error('Level must be between 1 and 3');
  }
  
  return {
    id,
    text: text.trim(),
    type,
    level,
    children: [],
    isExpanded: false,
    parentId
  };
};

/**
 * Recursively searches for a prediction by ID in nested prediction arrays
 * @param {string} id - The prediction ID to search for
 * @param {Array} predictionArray - Array of prediction objects to search
 * @returns {Object|null} Found prediction object or null if not found
 */
export const findPredictionById = (id, predictionArray) => {
  for (const prediction of predictionArray) {
    // Check if current prediction matches
    if (prediction.id === id) {
      return prediction;
    }
    
    // Recursively search in children
    if (prediction.children && prediction.children.length > 0) {
      const found = findPredictionById(id, prediction.children);
      if (found) {
        return found;
      }
    }
  }
  
  return null;
};

/**
 * Parses ChatGPT API response into a clean array of prediction texts
 * @param {string} response - Raw response from ChatGPT API
 * @returns {Array} Array of clean prediction text strings
 */
export const parseAPIResponse = (response) => {
  if (!response || typeof response !== 'string') {
    throw new Error('Invalid response: must be a non-empty string');
  }
  
  // Split by newlines and process each line
  const lines = response.split('\n');
  const cleanedPredictions = [];
  
  for (const line of lines) {
    // Remove empty lines
    if (!line.trim()) {
      continue;
    }
    
    // Remove common numbering patterns (1., 2., 3., etc. or 1), 2), 3), etc.)
    let cleanedLine = line.trim();
    cleanedLine = cleanedLine.replace(/^\d+[\.\)]\s*/, '');
    
    // Remove bullet points and dashes
    cleanedLine = cleanedLine.replace(/^[-•*]\s*/, '');
    
    // Only add if there's still content after cleaning
    if (cleanedLine.trim()) {
      cleanedPredictions.push(cleanedLine.trim());
    }
  }
  
  return cleanedPredictions;
};

/**
 * Validates that we received the expected number of predictions from the API
 * @param {Array} predictions - Array of prediction texts
 * @param {number} expectedCount - Expected number of predictions
 * @returns {boolean} True if count matches, false otherwise
 */
export const validatePredictionCount = (predictions, expectedCount) => {
  if (!Array.isArray(predictions)) {
    throw new Error('Predictions must be an array');
  }
  
  if (typeof expectedCount !== 'number' || expectedCount <= 0) {
    throw new Error('Expected count must be a positive number');
  }
  
  return predictions.length === expectedCount;
};

/**
 * Helper function to get all predictions at a specific level
 * @param {Array} predictionArray - Array of prediction objects
 * @param {number} targetLevel - The level to filter by
 * @returns {Array} Array of predictions at the specified level
 */
export const getPredictionsByLevel = (predictionArray, targetLevel) => {
  const result = [];
  
  const traverse = (predictions) => {
    for (const prediction of predictions) {
      if (prediction.level === targetLevel) {
        result.push(prediction);
      }
      
      if (prediction.children && prediction.children.length > 0) {
        traverse(prediction.children);
      }
    }
  };
  
  traverse(predictionArray);
  return result;
};

/**
 * Helper function to count total predictions in a nested structure
 * @param {Array} predictionArray - Array of prediction objects
 * @returns {number} Total count of all predictions
 */
export const getTotalPredictionCount = (predictionArray) => {
  let count = 0;
  
  const traverse = (predictions) => {
    for (const prediction of predictions) {
      count++;
      
      if (prediction.children && prediction.children.length > 0) {
        traverse(prediction.children);
      }
    }
  };
  
  traverse(predictionArray);
  return count;
};

/**
 * Updates a specific prediction in a nested prediction array
 * @param {Array} predictionArray - Array of prediction objects
 * @param {string} predictionId - ID of the prediction to update
 * @param {Object} updates - Object containing the updates to apply
 * @returns {Array} New array with the updated prediction
 */
export const updatePredictionById = (predictionArray, predictionId, updates) => {
  return predictionArray.map(prediction => {
    if (prediction.id === predictionId) {
      // Found the prediction to update
      return { ...prediction, ...updates };
    }
    
    // Check children recursively
    if (prediction.children && prediction.children.length > 0) {
      return {
        ...prediction,
        children: updatePredictionById(prediction.children, predictionId, updates)
      };
    }
    
    return prediction;
  });
};

/**
 * Adds child predictions to a specific parent prediction
 * @param {Array} predictionArray - Array of prediction objects
 * @param {string} parentId - ID of the parent prediction
 * @param {Array} childPredictions - Array of child prediction objects to add
 * @returns {Array} New array with the child predictions added
 */
export const addChildPredictions = (predictionArray, parentId, childPredictions) => {
  return predictionArray.map(prediction => {
    if (prediction.id === parentId) {
      // Found the parent prediction, add children
      return {
        ...prediction,
        children: [...prediction.children, ...childPredictions],
        isExpanded: true
      };
    }
    
    // Check children recursively
    if (prediction.children && prediction.children.length > 0) {
      return {
        ...prediction,
        children: addChildPredictions(prediction.children, parentId, childPredictions)
      };
    }
    
    return prediction;
  });
}; 